import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ShieldCheckIcon, ClipboardDocumentIcon, CheckIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import Cookies from 'universal-cookie';

export default function AdminJoinModal({ conference, handleClose }) {
    const [open, setOpen] = useState(true);
    const [copied, setCopied] = useState(false);
    const { t } = useTranslation();
    const cookies = new Cookies();

    const closeModal = () => {
        setOpen(false);
        handleClose && handleClose();
    };

    const adminJoinLink = conference 
        ? (() => {
            const baseUrl = window.location.origin;
            const laravelToken = cookies.get('token');
            const currentLang = localStorage.getItem('i18nextLng') || 'en';
            return `${baseUrl}/admin-join-setup.html?conference_id=${conference.id}&role=admin&api_token=${encodeURIComponent(laravelToken)}&lang=${currentLang}`;
        })()
        : '';

    const copyToClipboard = () => {
        navigator.clipboard.writeText(adminJoinLink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }).catch(() => {
            // Fallback for browsers that don't support clipboard API
            const textArea = document.createElement('textarea');
            textArea.value = adminJoinLink;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const openInNewTab = () => {
        window.open(adminJoinLink, '_blank', 'width=600,height=700,scrollbars=yes,resizable=yes');
        closeModal();
    };

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-10" onClose={closeModal}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                <div>
                                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-black">
                                        <ShieldCheckIcon
                                            className="h-6 w-6 text-white"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div className="mt-3 text-center sm:mt-5">
                                        <Dialog.Title
                                            as="h3"
                                            className="text-lg font-medium leading-6 text-gray-900"
                                        >
                                            {t('VideoConference.AdminJoinModal.Title')}
                                        </Dialog.Title>
                                        <div className="mt-2">
                                            <p className="text-sm text-gray-500">
                                                {t('VideoConference.AdminJoinModal.Description')}
                                            </p>
                                        </div>

                                        {/* Conference Title */}
                                        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                                            <p className="text-sm font-medium text-gray-900">
                                                {conference?.title}
                                            </p>
                                        </div>

                                        {/* Admin Link */}
                                        <div className="mt-4">
                                            <label className="block text-xs font-medium text-gray-700 text-left mb-1">
                                                {t('VideoConference.AdminJoinModal.AdminSetupLinkLabel')}
                                            </label>
                                            <div className="flex items-center">
                                                <input
                                                    type="text"
                                                    value={adminJoinLink}
                                                    readOnly
                                                    className="flex-1 block w-full rounded-l-md border-gray-300 text-sm bg-gray-50 text-gray-600 px-3 py-2 border focus:outline-none"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={copyToClipboard}
                                                    className={`inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md text-sm font-medium transition-colors ${
                                                        copied 
                                                            ? 'bg-green-50 text-green-700 border-green-300' 
                                                            : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                                                    }`}
                                                >
                                                    {copied ? (
                                                        <>
                                                            <CheckIcon className="h-4 w-4 mr-1" />
                                                            {t('VideoConference.AdminJoinModal.Copied')}
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ClipboardDocumentIcon className="h-4 w-4 mr-1" />
                                                            {t('VideoConference.AdminJoinModal.Copy')}
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Instructions */}
                                        <div className="mt-4 p-3 bg-yellow-50 rounded-lg">
                                            <p className="text-xs text-yellow-800">
                                                <strong>{t('VideoConference.AdminJoinModal.AdminPrivilegesTitle')}</strong> {t('VideoConference.AdminJoinModal.AdminPrivilegesDescription')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md border border-transparent bg-black px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 sm:text-sm"
                                        onClick={openInNewTab}
                                    >
                                        <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2" />
                                        {t('VideoConference.AdminJoinModal.OpenAdminSetup')}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 sm:mt-0 sm:text-sm"
                                        onClick={copyToClipboard}
                                    >
                                        <ClipboardDocumentIcon className="h-4 w-4 mr-2" />
                                        {copied ? t('VideoConference.AdminJoinModal.LinkCopied') : t('VideoConference.AdminJoinModal.CopyLink')}
                                    </button>
                                </div>

                                {/* Close Button */}
                                <div className="mt-3">
                                    <button
                                        type="button"
                                        className="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2 sm:text-sm"
                                        onClick={closeModal}
                                    >
                                        {t('VideoConference.AdminJoinModal.Close')}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}